import { ReactComponent as CloseIcon } from 'app/assets/img/close_icon.svg';
import ModalContainer from 'app/components/ModalContainer';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectTranslations } from '../App/slice/selectors';
import { FHT_FALLBACK_IMAGE } from './drawerOtherFHT.helpers';
import useWindowSize from 'app/hooks/useWindowSize';

type RoomImage = { url?: string } | string;
const MAX_THUMBS = 5;
interface RoomDetailModalProps {
  visible: boolean;
  onClose: () => void;
  roomOffer?: any;
}

const getImageUrl = (img: RoomImage): string =>
  typeof img === 'string' ? img : img?.url || '';

const RoomDetailModal: React.FC<RoomDetailModalProps> = ({
  visible,
  onClose,
  roomOffer,
}) => {
  const { device } = useWindowSize();
  const isMobile = device === 'mobile';
  const translations = useSelector(selectTranslations);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [thumbStartIndex, setThumbStartIndex] = useState(0);

  const imageList = useMemo(() => {
    const roomImages = roomOffer?.roomType?.images || [];
    const combined = [...roomImages].map(getImageUrl).filter(Boolean);
    return combined.length ? combined : [FHT_FALLBACK_IMAGE];
  }, [roomOffer]);
  const visibleImages = useMemo(
    () => imageList.slice(thumbStartIndex, thumbStartIndex + MAX_THUMBS),
    [imageList, thumbStartIndex],
  );
  useEffect(() => {
    if (visible) {
      setActiveImageIndex(0);
      setThumbStartIndex(0);
    }
  }, [visible, roomOffer]);
  const ensureActiveVisible = (index: number) => {
    if (index < thumbStartIndex) {
      setThumbStartIndex(index);
    } else if (index >= thumbStartIndex + MAX_THUMBS) {
      setThumbStartIndex(index - MAX_THUMBS + 1);
    }
  };
  const roomType = roomOffer?.roomType || {};
  const roomName = roomType?.name || '';
  const roomMeta = [roomType?.viewType].filter(Boolean).join(', ');
  const roomDescription =
    roomType?.description || roomOffer?.pricing?.rateDescription || '';

  const amenities = (roomType?.amenities || [])
    .map((a: any) => (typeof a === 'string' ? a : a?.name))
    .filter(Boolean);

  const moveImage = (step: number) => {
    if (imageList.length <= 1) return;
    const next =
      (activeImageIndex + step + imageList.length) % imageList.length;
    setActiveImageIndex(next);
    ensureActiveVisible(next);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = FHT_FALLBACK_IMAGE;
  };
  const handleThumbClick = (index: number) => {
    setActiveImageIndex(index);
    ensureActiveVisible(index);
  };
  const moveThumbWindow = (step: number) => {
    const maxStart = Math.max(imageList.length - MAX_THUMBS, 0);
    setThumbStartIndex(prev => Math.min(maxStart, Math.max(0, prev + step)));
  };

  return (
    <ModalContainer
      isShow={visible}
      onCloseDialog={onClose}
      maskClosable
      closable
      modalPadding="0"
      modalHeight="initial"
      CloseIcon={<CloseIcon width={12} height={12} />}
      modalProps={{ destroyOnClose: true, width: isMobile ? '100%' : 980 }}
      styleSlots={{
        iconWrapperStyle: { margin: 0 },
        modalContentStyle: {
          width: '100%',
          maxWidth: isMobile ? '100vw' : 980,
          maxHeight: isMobile ? '90vh' : '85vh',
          height: 'auto',
          padding: 0,
          borderRadius: isMobile ? 0 : 12,
        },
        modalStyle: { maxWidth: 'calc(100vw - 32px)' },
        modalBodyStyle: { padding: 0, overflow: 'hidden' },
      }}
    >
      <Root>
        <LeftPane>
          <MainImageWrap>
            <MainImage
              src={imageList[activeImageIndex]}
              alt={roomName}
              onError={handleImageError}
            />
            {imageList.length > 1 && (
              <>
                <NavButton
                  type="button"
                  style={{ left: 10 }}
                  onClick={() => moveImage(-1)}
                >
                  <i className="fa-solid fa-chevron-left" />
                </NavButton>
                <NavButton
                  type="button"
                  style={{ right: 10 }}
                  onClick={() => moveImage(1)}
                >
                  <i className="fa-solid fa-chevron-right" />
                </NavButton>
              </>
            )}
          </MainImageWrap>
          <ThumbContainer>
            {!isMobile && imageList.length > MAX_THUMBS && (
              <ThumbArrow
                type="button"
                disabled={thumbStartIndex === 0}
                onClick={() => moveThumbWindow(-1)}
              >
                <i className="fa-solid fa-chevron-left" />
              </ThumbArrow>
            )}

            <ThumbRow>
              {visibleImages.map((img, idx) => {
                const realIndex = thumbStartIndex + idx;

                return (
                  <ThumbBtn
                    key={`${img}-${realIndex}`}
                    type="button"
                    $active={realIndex === activeImageIndex}
                    onClick={() => handleThumbClick(realIndex)}
                  >
                    <Thumb
                      src={img}
                      alt={`${roomName}-${realIndex}`}
                      onError={handleImageError}
                    />
                  </ThumbBtn>
                );
              })}
            </ThumbRow>

            {!isMobile && imageList.length > MAX_THUMBS && (
              <ThumbArrow
                type="button"
                disabled={thumbStartIndex >= imageList.length - MAX_THUMBS}
                onClick={() => moveThumbWindow(1)}
              >
                <i className="fa-solid fa-chevron-right" />
              </ThumbArrow>
            )}
          </ThumbContainer>
        </LeftPane>
        <RightPane>
          <RoomName>{roomName}</RoomName>
          {roomMeta && <RoomMeta>{roomMeta}</RoomMeta>}
          {roomDescription && (
            <RoomDescription
              dangerouslySetInnerHTML={{ __html: roomDescription }}
            />
          )}
          <SectionTitle>
            {translations?.['b2b.txt.amenitiesAndServices']}
          </SectionTitle>
          <Amenities>
            {amenities.map((item: string) => (
              <AmenityItem key={item}>
                <i className="fa-regular fa-circle-check" />
                <span>{item}</span>
              </AmenityItem>
            ))}
          </Amenities>
        </RightPane>
      </Root>
    </ModalContainer>
  );
};

export default RoomDetailModal;

const Root = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 16px;
  padding: 14px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 12px;
  }
`;

const LeftPane = styled.div`
  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`;
const MainImageWrap = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
`;
const MainImage = styled.img`
  width: 100%;
  height: 320px;
  object-fit: cover;
  display: block;
  @media (max-width: 768px) {
    height: 55vw;
    max-height: 260px;
  }
`;
const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
`;
const ThumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;
const ThumbRow = styled.div`
  display: flex;
  gap: 8px;
  /* overflow: hidden; */
  @media (max-width: 768px) {
    width: max-content;
  }
`;
const ThumbArrow = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;
const ThumbBtn = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  border: ${({ $active }) =>
    $active ? '2px solid #f06f24' : '2px solid #e5e5e5'};
  border-radius: 8px;
  padding: 2px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease;
  &:hover {
    border-color: #f06f24;
  }
`;
const Thumb = styled.img`
  width: 72px;
  height: 52px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
  @media (max-width: 768px) {
    width: 60px;
    height: 44px;
  }
`;
const RightPane = styled.div`
  overflow-y: auto;
  max-height: 400px;
  @media (max-width: 768px) {
    overflow: visible;
    max-height: none;
  }
`;
const RoomName = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;
const RoomMeta = styled.div`
  margin-top: 6px;
  color: #666;
  font-size: 13px;
`;
const RoomDescription = styled.div`
  margin-top: 12px;
  color: #222;
  font-size: 13px;
  line-height: 1.5;
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;
const SectionTitle = styled.h4`
  margin: 14px 0 8px;
  font-size: 14px;
  font-weight: 600;
`;
const Amenities = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
`;
const AmenityItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #333;
`;
